import { localizePath as __lp } from "@/i18n/locales";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FAQSection from "@/components/FAQSection";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useLocale } from "@/i18n/LocaleProvider";
import EstimatorConsole from "@/components/pricing/EstimatorConsole";
import RangeChart from "@/components/pricing/RangeChart";
import { PRICING_COPY, TIERS, type Lang } from "@/data/pricing";

const SITE = "https://devcore.systems";

const Pricing = () => {
  const { lang, localize } = useLocale();
  const c = PRICING_COPY[(lang as Lang) in PRICING_COPY ? (lang as Lang) : "en"];
  const langPath = lang === "en" ? "" : `/${lang}`;
  const canonical = `${SITE}${__lp("/pricing", lang)}`;
  const fmt = (k: number) =>
    new Intl.NumberFormat(lang === "en" ? "en-IE" : lang, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(k * 1000);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        name: c.metaTitle,
        description: c.metaDescription,
        url: canonical,
        inLanguage: lang,
        publisher: { "@id": `${SITE}/#organization` },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: c.tableTitle,
        provider: { "@id": `${SITE}/#organization` },
        url: canonical,
        offers: TIERS.map((tier) => ({
          "@type": "AggregateOffer",
          name: c.tiers[tier.id].name,
          description: c.tiers[tier.id].desc,
          priceCurrency: "EUR",
          lowPrice: tier.price[0] * 1000,
          highPrice: tier.price[1] * 1000,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: c.home, item: `${SITE}${langPath || "/"}` },
          { "@type": "ListItem", position: 2, name: c.eyebrow, item: canonical },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: c.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
    [c, canonical, lang, langPath],
  );

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={c.metaTitle} description={c.metaDescription} canonical={canonical} ogType="website" jsonLd={jsonLd} />
      <Navbar />

      <header className="relative overflow-hidden pt-24 sm:pt-32 pb-12 md:pt-44 md:pb-20">
        <div className="absolute inset-0 tech-grid pointer-events-none opacity-[0.12]" />
        <div className="container relative z-10">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <li>
                <Link to={localize("/")} className="transition-colors hover:text-accent">{c.home}</Link>
              </li>
              <li aria-hidden className="text-border">/</li>
              <li className="text-foreground">{c.eyebrow}</li>
            </ol>
          </nav>
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] [&>*]:min-w-0">
          <ScrollReveal className="max-w-4xl">
            <h1 className="text-[1.9rem] sm:text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-6xl">
              {c.title} <span className="gradient-text">{c.titleAccent}</span>
            </h1>
            <p className="mt-7 max-w-2xl text-[15px] sm:text-lg leading-relaxed text-muted-foreground md:text-xl">{c.subtitle}</p>
          </ScrollReveal>
          <ScrollReveal delay={150}>
            <EstimatorConsole
              names={Object.fromEntries(TIERS.map((t) => [t.id, c.tiers[t.id].name]))}
              fmt={fmt}
              weeksLabel={c.weeks}
            />
          </ScrollReveal>
          </div>
        </div>
      </header>

      <section className="border-t border-border py-14 sm:py-20">
        <div className="container">
          <ScrollReveal className="mb-10 max-w-2xl">
            <p className="mono-label mb-4">{c.tableKicker}</p>
            <h2 className="text-[1.55rem] sm:text-3xl font-bold tracking-tight text-foreground md:text-4xl">{c.tableTitle}</h2>
          </ScrollReveal>
          <div className="grid gap-4">
            {TIERS.map((tier, i) => (
              <ScrollReveal key={tier.id} delay={i * 70}>
                <article className="grid gap-4 rounded-2xl border border-border bg-card/40 p-6 md:grid-cols-[minmax(0,1.6fr)_1fr_1fr_1fr] md:items-center">
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{c.tiers[tier.id].name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{c.tiers[tier.id].desc}</p>
                  </div>
                  <div>
                    <p className="mono-label text-muted-foreground">{c.colPrice}</p>
                    <p className="mt-1 font-heading text-xl font-bold gradient-text">
                      {fmt(tier.price[0])} – {fmt(tier.price[1])}
                    </p>
                  </div>
                  <div>
                    <p className="mono-label text-muted-foreground">{c.colTime}</p>
                    <p className="mt-1 text-foreground">{tier.weeks[0]}–{tier.weeks[1]} {c.weeks}</p>
                  </div>
                  <div>
                    <p className="mono-label text-muted-foreground">{c.colEffort}</p>
                    <p className="mt-1 text-foreground">{tier.days[0]}–{tier.days[1]} {c.days}</p>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">{c.note}</p>
          <RangeChart
            rows={TIERS.map((t) => ({ id: t.id, name: c.tiers[t.id].name, price: t.price, weeks: t.weeks }))}
            fmt={fmt}
            weeksLabel={c.weeks}
          />
        </div>
      </section>

      <section className="border-t border-border bg-secondary/20 py-14 sm:py-20">
        <div className="container">
          <ScrollReveal className="mb-10 max-w-2xl">
            <p className="mono-label mb-4">{c.driversKicker}</p>
            <h2 className="text-[1.55rem] sm:text-3xl font-bold tracking-tight text-foreground md:text-4xl">{c.driversTitle}</h2>
          </ScrollReveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {c.drivers.map((d, i) => (
              <ScrollReveal key={d.title} delay={i * 70}>
                <div className="h-full rounded-2xl border border-border bg-card/40 p-6">
                  <h3 className="text-lg font-bold text-foreground">{d.title}</h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{d.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <Link
            to={localize("/pages/custom-software-development")}
            className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline"
          >
            {{ en: "How custom software development works with us", de: "So läuft individuelle Softwareentwicklung bei uns", el: "Πώς δουλεύουμε την ανάπτυξη εξατομικευμένου λογισμικού" }[lang]}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="border-t border-border py-14 sm:py-20">
        <div className="container max-w-3xl text-center">
          <h2 className="text-[1.55rem] sm:text-3xl font-bold tracking-tight text-foreground md:text-4xl">{c.ctaTitle}</h2>
          <p className="mt-4 text-muted-foreground md:text-lg">{c.ctaBody}</p>
          <span className="hover-glow-accent mt-8 inline-block">
            <Link
              to={localize("/blueprint")}
              className="group inline-flex items-center gap-2 btn-clip bg-primary px-8 py-4 text-lg font-semibold text-primary-foreground btn-label-contrast"
            >
              {c.ctaButton}
              <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </span>
        </div>
      </section>

      <FAQSection
        id="pricing-faq"
        label={c.faqKicker}
        headingA={c.faqTitle}
        items={c.faqs.map((f) => ({ question: f.q, answer: f.a }))}
      />
      <Footer />
    </div>
  );
};

export default Pricing;
